export const CLASS_COLOR_NEUTRAL = 'white' as const
// Persisted studio-configurable class colors; these are not Fitro brand tokens.
const CLASS_COLOR_PALETTE = [
    'rose',
    'orange',
    'amber',
    'yellow',
    'lime',
    'green',
    'teal',
    'sky',
    'indigo',
    'violet',
    'pink',
    'fuchsia',
    'purple',
    'blue',
    'cyan',
] as const

export type ClassColorKey = (typeof CLASS_COLOR_PALETTE)[number] | typeof CLASS_COLOR_NEUTRAL

const normalizeValue = (value?: string | null) => (value ?? '').trim().toLowerCase()
type PaletteColorKey = (typeof CLASS_COLOR_PALETTE)[number]
const DEFAULT_CLASS_COLOR_PALETTE = CLASS_COLOR_PALETTE.slice(0, 10) as readonly PaletteColorKey[]
const ALL_CLASS_COLOR_KEYS = [CLASS_COLOR_NEUTRAL, ...CLASS_COLOR_PALETTE] as const

const stableSeedHash = (value: string): number => {
    let hash = 2166136261
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index)
        hash = Math.imul(hash, 16777619)
    }
    return hash >>> 0
}

const isClassColorKey = (value?: string | null): value is ClassColorKey => {
    if (!value) return false
    const normalized = normalizeValue(value)
    return (ALL_CLASS_COLOR_KEYS as readonly string[]).includes(normalized)
}

export const normalizeClassColorKey = (value?: string | null): ClassColorKey | null => {
    const normalized = normalizeValue(value)
    return isClassColorKey(normalized) ? (normalized as ClassColorKey) : null
}

const resolveClassColorKey = (value?: string | null, seed?: string | null): ClassColorKey => {
    const normalized = normalizeClassColorKey(value)
    if (normalized) {
        return normalized
    }
    const palette = DEFAULT_CLASS_COLOR_PALETTE
    const resolvedSeed = seed ?? ''
    if (!resolvedSeed) {
        return palette[0]
    }
    let hash = 0
    for (let i = 0; i < resolvedSeed.length; i += 1) {
        hash = (hash + resolvedSeed.charCodeAt(i)) % palette.length
    }
    return palette[Math.abs(hash) % palette.length]
}

export const getClassColorToken = (value?: string | null, seed?: string | null): string => {
    const resolved = resolveClassColorKey(value, seed)
    return `var(--class-color-${resolved})`
}

export const getClassColorAccentToken = (value?: string | null, seed?: string | null): string => {
    const resolved = resolveClassColorKey(value, seed)
    return `var(--class-color-accent-${resolved})`
}

export const getClassCoverGradient = (value?: string | null, seed?: string | null): string => {
    const hash = stableSeedHash(`${seed ?? ''}:${normalizeValue(value)}`)
    const resolved = resolveClassColorKey(value, seed)
    const primary = resolved === CLASS_COLOR_NEUTRAL
        ? CLASS_COLOR_PALETTE[hash % CLASS_COLOR_PALETTE.length]
        : resolved
    const primaryIndex = CLASS_COLOR_PALETTE.indexOf(primary)
    const secondary = CLASS_COLOR_PALETTE[
        (primaryIndex + 1 + (hash % (CLASS_COLOR_PALETTE.length - 1))) % CLASS_COLOR_PALETTE.length
    ]
    const angle = 105 + (hash % 61)
    const highlightX = 20 + ((hash >>> 8) % 61)

    return `radial-gradient(circle at ${highlightX}% 20%, color-mix(in srgb, var(--class-color-accent-${secondary}) 38%, transparent) 0%, transparent 48%), linear-gradient(${angle}deg, var(--class-color-${primary}) 0%, color-mix(in srgb, var(--class-color-${secondary}) 72%, var(--class-color-accent-${primary})) 100%)`
}
